/* eslint-disable react/self-closing-comp */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { format } from 'd3-format';
import * as d3 from 'd3-selection';
import { extent as arrayExtent } from 'd3-array';
import { scaleOrdinal } from 'd3-scale';
import { stack } from 'd3-shape';
import { timeMinute } from 'd3-time';

import widgetWrapper from './widgetWrapper';

import './BarChartWidget.scss';

// TODO: dispatcher stuff
// import { dispatch } from 'd3-dispatch';

import {
  chartAxis,
  chartAxisLabel,
  chartLegend,
  colorScheme,
  formatTimestamp,
  getScaleType,
} from './widgetUtils';

class BarChartWidget extends Component {

  static propTypes = {
    series: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.number.isRequired })),
      keys: PropTypes.arrayOf(PropTypes.string),
      xDomain: PropTypes.arrayOf(PropTypes.number),
      yDomain: PropTypes.arrayOf(PropTypes.number),
      yAxis: PropTypes.shape({
        label: PropTypes.string,
        type: PropTypes.string,
        tickArguments: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
        tickValues: PropTypes.object,
      }),
      margin: PropTypes.shape({
        top: PropTypes.number,
        right: PropTypes.number,
        left: PropTypes.number,
        bottom: PropTypes.number,
      }),
      colors: PropTypes.arrayOf(PropTypes.string),
    }),
    title: PropTypes.string,
    subtitle: PropTypes.string,
    timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
  };

  static defaultProps = {
    title: '',
    subtitle: '',
    timestamp: '',
    className: '',
    series: {},
  };

  constructor(...args) {
    super(...args);
    this.chartRef = React.createRef();
    this.legendRef = React.createRef();
  }

  componentDidMount() {
    window.addEventListener('resize', this.doD3Rendering);
    this.doD3Rendering();
  }

  componentDidUpdate(_prevProps, _prevState) {
    this.doD3Rendering();
  }

  doD3Rendering() {
    if (this.props.series) {
      const series = this.props.series;
      const {
        data: dataArray = [],
        keys = ['value'],
        margin: {
          top: marginTop = 10,
          right: marginHeight = 40,
          left: marginLeft = 40,
          bottom: marginBottom = 30,
        } = {},
        colors = [],
      } = series;

      const xAxisProps = Object.assign({
        label: '',
        type: 'time',
        grid: true,
        orient: 'bottom',
        labelPadding: 6,
        tickArguments: [],
        tickValues: null,
        tickFormat: null,
        tickSize: 6,
        tickSizeInner: 6,
        tickSizeOuter: 6,
        tickPadding: 3,
        tickIntervalMinutes: null, // non-d3 field
      }, series.xAxis);

      if (xAxisProps.tickIntervalMinutes) {
        xAxisProps.tickArguments = [timeMinute.every(xAxisProps.tickIntervalMinutes)];
      }

      const yAxisProps = Object.assign({
        label: '',
        type: 'linear',
        grid: true,
        orient: 'left',
        labelPadding: 6,
        tickArguments: [],
        tickValues: null,
        tickFormat: null,
        tickSize: 6,
        tickSizeInner: 6,
        tickSizeOuter: 6,
        tickPadding: 3,
      }, series.yAxis);

      if (typeof xAxisProps.tickFormat === 'string') {
        xAxisProps.tickFormat = format(xAxisProps.tickFormat);
      }
      if (typeof yAxisProps.tickFormat === 'string') {
        yAxisProps.tickFormat = format(yAxisProps.tickFormat);
      }

      // Persist the color scale.
      const scheme = colors instanceof Array && colors.length > 0 ? colors : colorScheme;

      const colorScale = this.colorScale
        || (this.colorScale = scaleOrdinal().range(scheme));

      const xAccessor = (d => d.key);
      const colorAccessor = (k => colorScale(k));
      const getDatumKey = d => xAccessor(d.data);

      let svg = d3.select(this.chartRef.current)
        .data([0]);
      svg = svg
        .enter()
        .append('svg')
        .attr('class', 'bar-chart')
        .merge(svg);

      const legend = d3.select(this.legendRef.current)
        .data([keys.length > 1 ? keys : []]);
      legend.enter()
        .append('ul')
        .attr('class', 'legend')
        .merge(legend)
        .call(chartLegend, colorAccessor);

      const width = svg.node().parentNode.clientWidth;
      const height = svg.node().parentNode.clientHeight;
      const chartWidth = width - marginLeft - marginHeight;
      const chartHeight = height - marginTop - marginBottom;

      if (chartWidth <= 0 || chartHeight <= 0) {
        console.log(`BarChartWidget: chartWidth is ${chartWidth} and chartHeight is ${chartHeight}, returning.`);
        return;
      }

      svg
        .attr('width', width)
        .attr('height', height);

      // Set the chart margin.
      let margin = svg.selectAll('.margin')
        .data([0]);
      margin = margin.enter()
        .append('g')
        .attr('class', 'margin')
        .merge(margin)
        .attr('transform', `translate(${marginLeft}, ${marginTop})`);

      // Stack the bars
      const stacked = stack().keys(keys);
      const grouped = stacked(dataArray);

      // Examine the data for domains.
      let extent;
      let xDomain = series.xDomain
        || arrayExtent(dataArray, xAccessor);
      // Need to widen domain by the width of one bar:
      const [xmin, xmax] = xDomain;
      const cen = (xmin + xmax) / 2;
      xDomain = [
        xmin - ((cen - xmin) / dataArray.length),
        xmax + ((xmax - cen) / dataArray.length),
      ];
      const BAR_WIDTH = (chartWidth / (dataArray.length + 1)) * 0.9; // a pixel on each side
      const yDomain = series.yDomain
        || grouped.reduce((e, s) => {
          extent = arrayExtent(s, d => d[1]); // d[1] is the position of the top of stack segment
          if (e[0] === undefined || extent[0] < e[0]) {
            e[0] = extent[0];
          }
          if (e[1] === undefined || extent[1] > e[1]) {
            e[1] = extent[1];
          }
          return e;
        }, []);
      yDomain[0] = 0; // Bar chart starts at 0


      const noData = (xDomain[0] === xDomain[1] || yDomain[0] === yDomain[1]);
      if (noData) {
        margin.append('g')
          .attr('transform', `translate(${chartWidth / 2},${chartHeight / 2})`)
          .append('text')
          .attr('class', 'no-data')
          .attr('text-anchor', 'center')
          .text('NO DATA');
      } else {

        // Create the scales.
        const xScale = (getScaleType(xAxisProps.type))()
          .domain(xDomain)
          .range([0, chartWidth]);
        const yScale = (getScaleType(yAxisProps.type))()
          .domain(yDomain)
          .range([chartHeight, 0])
          .nice();

        // Draw the axes
        const axis = margin.selectAll('.axis')
          .data([xAxisProps, yAxisProps]);
        axis.enter()
          .append('g')
          .attr('class', (d, i) => `axis ${i ? 'y' : 'x'}`)
          .merge(axis)
          .call(chartAxis, xScale, yScale);

        const flattened = grouped.reduce((f, s) => {
          s.forEach((d) => {
            if ((!Number.isNaN(d[0])) && (!Number.isNaN(d[1]))) {
              f.push({
                colorKey: s.key, // color
                key: d.data.key,
                min: d[0],
                max: d[1],
                data: d.data,
              });
            }
          });
          return f;
        }, []);

        const getX = (d, i) => {
          const datumKey = getDatumKey(d, i);
          if (!datumKey) {
            return -4000; // Just get it off the screen if we can't figure out where it goes
          }
          return xScale(getDatumKey(d, i)) - (BAR_WIDTH / 2);
        };
        const getY = (d => yScale(d.max));
        const getHeight = (d => Math.max(0, (yScale(d.min) - yScale(d.max))));

        const bars = margin.selectAll('.bar').data(flattened);
        bars.exit().remove();
        bars.enter()
          .append('rect').attr('class', 'bar')
          .merge(bars)
          .attr('x', getX)
          .attr('y', getY)
          .attr('width', BAR_WIDTH)
          .attr('height', getHeight)
          .style('fill', d => colorAccessor(d.colorKey));
        // Create the axis labels. They are layered above the chart content.
        const axisLabel = margin.selectAll('.label')
          .data([[xAxisProps, yAxisProps.orient], [yAxisProps, xAxisProps.orient]]);
        axisLabel.enter()
          .append('text')
          .attr('class', (d, i) => `label ${i ? 'y' : 'x'}`)
          .merge(axisLabel)
          .call(chartAxisLabel, xScale, yScale);
      }
    }
  }

  render() {
    const {
      title = '',
      subtitle = '',
      timestamp = '',
      className = '',
    } = this.props;
    return (
      <div className={`BarChartWidget ${className} widget-threshold-colors pdf-colors`}>
        <div className="header">
          <div className="title">{title}</div>
          <div className="subtitle">{subtitle}</div>
        </div>
        <div className="content">
          <svg className="bar-chart" ref={this.chartRef} />
        </div>
        <div className="footer">
          <ul className="legend" ref={this.legendRef} />
          <div className="timestamp">{formatTimestamp(timestamp)}</div>
        </div>
      </div>
    );
  }
}

export default widgetWrapper(BarChartWidget);
